// Выгрузка журнала действий в фоне (002: data-model.md, audit.exports; contracts/api.md,
// `AuditExport`; FR-051 – FR-056). Фильтры заказа хранятся только в заказе (FR-010а); в ответе —
// словами, как в чипах журнала (filtersSummary).
import { z } from '../kernel/zod.ts';
import { ErrorCodeSchema, errorCatalog } from '../errors/catalog.ts';
import type { ErrorCode } from '../errors/catalog.ts';
import { eventCatalog, notificationLinks } from '../events/catalog.ts';
import { IdSchema, OperationIdSchema, TimestampSchema } from '../kernel/common.ts';
import type { NotificationAction } from '../kernel/notification.ts';
import { formatAuditTime } from './changes.ts';
import { AuditResultSchema } from './entry.ts';
import type { AuditResult } from './entry.ts';
import { auditOperationLabel, auditResultLabels, entityTypeLabel, fieldLabel } from './labels.ts';

/** Больше — выгрузку не заказать: 413 REC-013 (FR-052б). */
export const AUDIT_EXPORT_BACKGROUND_LIMIT = 500_000;

/** preparing — готовится, ready — готов, failed — не удалась, deleted — удалён. */
export const auditExportStates = ['preparing', 'ready', 'failed', 'deleted'] as const;
export type AuditExportState = (typeof auditExportStates)[number];
export const AuditExportStateSchema = z.enum(auditExportStates);

/** Состояние словом — бейдж «Мои выгрузки» (contracts/ui.md). */
export const auditExportStateLabels = {
  preparing: 'Готовится',
  ready: 'Готов',
  failed: 'Не удалась',
  deleted: 'Удалён',
} as const satisfies Record<AuditExportState, string>;

/**
 * Условия заказа — как в теле POST /api/audit/exports: «Заказать снова» повторяет их. Видит их
 * только заказавший; в задачу, технический журнал и оповещения они не попадают (FR-010а).
 */
export const AuditExportFiltersSchema = z.object({
  q: z.string().optional(),
  actor: IdSchema.optional(),
  operation: z.string().optional(),
  entityType: z.string().optional(),
  entityId: IdSchema.optional(),
  aboutUser: IdSchema.optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  result: AuditResultSchema.optional(),
  sort: z.enum(['at', '-at']).optional(),
});

export const AuditExportSchema = z.object({
  id: IdSchema,
  state: AuditExportStateSchema,
  /** Фильтры словами: «Поиск: «протокол» · Итог: Отказ». */
  filtersSummary: z.string(),
  filters: AuditExportFiltersSchema,
  /** Момент заказа: в файл идут записи, вставленные в журнал до него (FR-052). */
  snapshotAt: TimestampSchema,
  recordsCount: z.number().int().nonnegative().optional(),
  /** Файл хранится до (FR-055). */
  expiresAt: TimestampSchema.optional(),
  /** Код ошибки, если выгрузка не удалась (FR-053а). */
  errorCode: ErrorCodeSchema.optional(),
  /** Номер операции заказа. */
  operation: OperationIdSchema,
  createdAt: TimestampSchema,
  finishedAt: TimestampSchema.optional(),
});
export type AuditExport = z.infer<typeof AuditExportSchema>;

/**
 * Причина неудачи выгрузки словами (FR-053а): текст кода из каталога ошибок — у OPS-003
 * «Выгрузку отменило сопровождение» (FR-008б). Текст с меткой без значения — до метки:
 * «Слишком большая выгрузка» у REC-013 без числа; метка в начале — «Файл не собран».
 */
export function exportFailureReason(code: ErrorCode): string {
  const { message } = errorCatalog[code];
  const mark = message.indexOf('{');
  if (mark < 0) return message;
  const head = message.slice(0, mark).replace(/[\s:,—-]+$/, '');
  return head === '' ? 'Файл не собран' : head;
}

export interface ExportFailedNotice {
  readonly title: string;
  /** Причина словами — без кода и номера: их лента показывает отдельной строкой. */
  readonly body: string;
  readonly code: ErrorCode;
  /** Номер операции заказа. */
  readonly operation: string;
  /** «Мои выгрузки». */
  readonly link: string;
  /** «Заказать снова» — «Мои выгрузки» на этом заказе. */
  readonly action: NotificationAction;
}

const failedRule = eventCatalog['audit.export.failed'].notifications[0];

/**
 * Уведомление заказавшему «Выгрузка журнала не удалась» (FR-053а, FR-008б): причина словами,
 * код и номер операции заказа — отдельными полями (лента показывает «Код … · операция …»
 * с «Скопировать номер»), кнопка «Заказать снова». Один текст у неудачи сборки и у отмены задачи
 * сопровождением. reason — причина с подробностью, которой нет в коде (число записей у REC-013).
 */
export function exportFailedNotice(input: {
  readonly exportId: string;
  readonly code: ErrorCode;
  readonly operation: string;
  readonly reason?: string | undefined;
}): ExportFailedNotice {
  return {
    title: failedRule.title,
    body: input.reason ?? exportFailureReason(input.code),
    code: input.code,
    operation: input.operation,
    link: notificationLinks[failedRule.link],
    action: {
      label: failedRule.action.label,
      link: notificationLinks[failedRule.action.link].replace('{id}', input.exportId),
    },
  };
}

/** Поиск и фильтры заказа — как AuditFiltersSchema (api/audit.ts) после проверки. */
export interface AuditExportFilters {
  readonly q?: string | undefined;
  readonly actor?: string | undefined;
  readonly aboutUser?: string | undefined;
  readonly operation?: string | undefined;
  readonly entityType?: string | undefined;
  readonly entityId?: string | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly result?: AuditResult | undefined;
}

/** Порядок условий в описании — как в форме фильтров журнала. */
const SUMMARY_ORDER = [
  'q',
  'actor',
  'aboutUser',
  'operation',
  'entityType',
  'entityId',
  'from',
  'to',
  'result',
] as const satisfies readonly (keyof AuditExportFilters)[];

/**
 * Пользователь в условиях заказа, которого заказавший не видит (T115): ФИО в описании — только
 * тех, кого он видит в справочнике пользователей или в своём журнале.
 */
export const unseenPersonLabel = 'пользователь вне ваших областей';

export interface FiltersSummaryOptions {
  /** Часовой пояс установки — для периода. */
  readonly timeZone: string;
  /**
   * ФИО по номеру пользователя: автор, «О пользователе», запись-пользователь; кого заказавший
   * не видит — unseenPersonLabel. Номера нет — показывается сам номер.
   */
  readonly names?: ReadonlyMap<string, string>;
}

/** Условия заказа словами, как в чипах журнала; без условий — «Без условий». */
export function filtersSummary(
  filters: AuditExportFilters,
  options: FiltersSummaryOptions,
): string {
  const person = (id: string): string => options.names?.get(id) ?? id;
  const parts: string[] = [];
  for (const key of SUMMARY_ORDER) {
    const value = filters[key];
    if (value === undefined || value === '') continue;
    let text: string;
    switch (key) {
      case 'q':
        text = `«${value}»`;
        break;
      case 'actor':
      case 'aboutUser':
      case 'entityId':
        text = person(value);
        break;
      case 'operation':
        text = auditOperationLabel(value);
        break;
      case 'entityType':
        text = entityTypeLabel(value);
        break;
      case 'from':
      case 'to':
        text = formatAuditTime(value, options.timeZone);
        break;
      case 'result':
        text = auditResultLabels[filters.result!];
        break;
    }
    parts.push(`${fieldLabel(key)}: ${text}`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'Без условий';
}
